import { inject, Injectable } from '@angular/core';

import { EnableClassTelemetry } from 'app/services/otel.service';
import { StorageAPIService } from '../storage-api.service';
import { DownloadTasksService, Task, TaskPayload, TaskType } from '../tasks-api/download.service';

export type AuditEventType =
  | 'account-created'
  | 'action-plan-created'
  | 'action-plan-deleted'
  | 'action-plan-updated'
  | 'action-plan-item-created'
  | 'action-plan-item-deleted'
  | 'action-plan-item-updated'
  | 'action-plan-task-created'
  | 'action-plan-task-deleted'
  | 'action-plan-task-updated'
  | 'alias-created'
  | 'alias-deleted'
  | 'analysis-complete'
  | 'analysis-created'
  | 'analysis-deleted'
  | 'analysis-requeued'
  | 'analysisgroup-created'
  | 'analysisgroup-deleted'
  | 'annotation-created'
  | 'collection-created'
  | 'collection-deleted'
  | 'collection-locked'
  | 'configuration-created'
  | 'configuration-deleted'
  | 'configuration-updated'
  | 'configuration-imported'
  | 'configuration-tag-added'
  | 'configuration-tag-removed'
  | 'configuration-user-entity-created'
  | 'configuration-user-entity-deleted'
  | 'configuration-user-entity-updated'
  | 'configuration-user-entities-created'
  | 'configuration-user-entities-deleted'
  | 'configuration-user-entities-updated'
  | 'configuration-all-user-entities-deleted'
  | 'configuration-phrase-created'
  | 'configuration-phrase-deleted'
  | 'configuration-phrase-updated'
  | 'configuration-phrases-created'
  | 'configuration-phrases-deleted'
  | 'configuration-phrases-updated'
  | 'configuration-all-phrases-deleted'
  | 'configuration-topic-created'
  | 'configuration-topic-deleted'
  | 'configuration-topic-updated'
  | 'configuration-topics-created'
  | 'configuration-topics-deleted'
  | 'configuration-topics-updated'
  | 'configuration-all-topics-deleted'
  | 'configuration-concept-topic-created'
  | 'configuration-concept-topic-deleted'
  | 'configuration-concept-topic-updated'
  | 'configuration-concept-topics-created'
  | 'configuration-concept-topics-deleted'
  | 'configuration-concept-topics-updated'
  | 'configuration-all-concept-topics-deleted'
  | 'configuration-taxonomy-created'
  | 'configuration-taxonomy-deleted'
  | 'configuration-taxonomy-updated'
  | 'configuration-taxonomies-created'
  | 'configuration-taxonomies-deleted'
  | 'configuration-taxonomies-updated'
  | 'configuration-all-taxonomies-deleted'
  | 'configuration-blacklist-entry-created'
  | 'configuration-blacklist-entry-deleted'
  | 'configuration-blacklist-entry-updated'
  | 'configuration-blacklist-entries-created'
  | 'configuration-blacklist-entries-deleted'
  | 'configuration-blacklist-entries-updated'
  | 'configuration-all-blacklist-entries-deleted'
  | 'configuration-route-created'
  | 'configuration-route-deleted'
  | 'configuration-route-updated'
  | 'dashboard-cloned'
  | 'dashboard-created'
  | 'dashboard-deleted'
  | 'dashboard-locked'
  | 'dashboard-shared'
  | 'dashboard-unlocked'
  | 'dashboard-updated'
  | 'docs-created'
  | 'group-created'
  | 'group-updated'
  | 'group-deleted'
  | 'group-member-added'
  | 'group-member-deleted'
  | 'group-members-added'
  | 'group-permission-added'
  | 'group-permission-deleted'
  | 'insight-deleted'
  | 'metadata-created'
  | 'metadata-deleted'
  | 'metadata-updated'
  | 'project-created'
  | 'project-deleted'
  | 'task-created'
  | 'task-deleted'
  | 'test-document-generated'
  | 'upload-cancelled'
  | 'upload-complete'
  | 'upload-created'
  | 'upload-deleted'
  | 'user-created'
  | 'user-updated'
  | 'user-permission-added'
  | 'user-permission-deleted'
  | 'user-login'
  | 'user-login-failed'
  | 'query-topic-purpose-generated';

export const REPORTABLE_AUDIT_EVENTS = [
  'analysis-created',
  'analysis-deleted',
  'analysis-requeued',
  'analysisgroup-created',
  'analysisgroup-deleted',
  'collection-created',
  'collection-deleted',
  'dashboard-created',
  'dashboard-deleted',
  'dashboard-updated',
  'metadata-created',
  'metadata-deleted',
  'metadata-updated',
  'project-created',
  'project-deleted',
  'user-login',
  'user-login-failed',
];

export interface AuditEvent {
  id: string;
  timestamp: string;
  type: 'EVENT';
  name: AuditEventType;
  category: string;
  properties: { [key: string]: string };
  metrics?: { [key: string]: number };
}

export interface AuditListResponse {
  count: number;
  items: AuditEvent[];
  aggregations: {
    name: {
      buckets: { key: string; count: number }[];
    };
    category: {
      buckets: { key: string; count: number }[];
    };
  };
}

interface AuditReportTaskPayload extends TaskPayload {
  newerThan: string;
  olderThan: string;
  timezone: string;
  filename?: string;
  name?: string;
  category?: string;
}

interface AuditReportResult {
  count: number;
  exportId: string;
  fileSize: number;
}

export type AuditReportTask = Task<AuditReportTaskPayload, AuditReportResult>;

@Injectable()
@EnableClassTelemetry()
export class AuditService extends StorageAPIService {
  private taskService = inject(DownloadTasksService);

  listAuditEvents(
    nameOrcategory: string,
    from: string,
    to: string,
    offset?: number,
    limit?: number
  ): Promise<AuditListResponse> {
    const queryParams = {
      newerThan: from,
      olderThan: to,
      offset,
      limit,
      ...this.extractNameOrCategory(nameOrcategory),
    };

    return this.get('/audit/', queryParams);
  }

  logEvent(name: AuditEventType, properties: Record<string, string>): Promise<AuditEvent> {
    return this.post('/audit/', {
      name: name,
      properties: properties,
    });
  }

  createAuditReportTask(
    nameOrcategory: string,
    from: string,
    to: string,
    timezone: string
  ): Promise<AuditReportTask> {
    const payload: AuditReportTaskPayload = {
      _type: TaskType.AUDIT_REPORT,
      newerThan: from,
      olderThan: to,
      timezone,
      metadata: {
        source: 'Report',
      },
      ...this.extractNameOrCategory(nameOrcategory),
    };

    return this.taskService.createTask<AuditReportTaskPayload, AuditReportResult>(payload);
  }

  private extractNameOrCategory(nameOrcategory) {
    if (nameOrcategory.includes('-')) {
      return { name: nameOrcategory };
    } else {
      return { category: nameOrcategory };
    }
  }
}
